import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type TransactionType =
  | 'order_payment'
  | 'user_to_user_with_fee'
  | 'user_to_user_no_fee'
  | 'commission_payment'
  | 'system_commission_payment'
  | 'account_payment'
  | 'payout_payment'
  | 'pool_payout_payment';

export type TransactionStatus = 'pending' | 'completed' | 'failed' | 'cancelled';

export interface EwalletTransaction {
  id: number;
  reference_number: string;
  transaction_type: TransactionType;
  status: TransactionStatus;
  currency?: string;
  amount: number;
  fee: number;
  total_amount: number;
  subject?: string;
  notes?: string;
  created_at: string;
  // Sender info
  sender_id: number;
  sender_email?: string;
  sender_username?: string;
  sender_full_name?: string;
  sender_balance_before?: number;
  sender_balance_after?: number;
  // Recipient info
  recipient_id?: number;
  recipient_email?: string;
  recipient_username?: string;
  recipient_full_name?: string;
  recipient_balance_before?: number;
  recipient_balance_after?: number;
  // Related entities
  order_id?: number;
  payout_id?: number;
}

export interface EwalletStatisticsFilters {
  filter_user_ids?: number[];
  filter_sender_ids?: number[];
  filter_recipient_ids?: number[];
  filter_transaction_types?: TransactionType[];
  filter_status?: TransactionStatus;
  filter_start_date?: string;
  filter_end_date?: string;
  filter_amount_min?: number;
  filter_amount_max?: number;
  page?: number;
  per_page?: number;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface TransactionListResponse {
  transactions: EwalletTransaction[];
  total_count: number;
  total_amount_sum: number;
  total_fee_sum: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ChartDataPoint {
  date: string;
  count: number;
  total_amount: number;
  total_fee: number;
}

export interface BalanceDataPoint {
  date: string;
  balance_after: number;
  transaction_id: number;
}

export interface ChartDataResponse {
  interval: 'day' | 'week' | 'month';
  start_date: string;
  end_date: string;
  data_points: ChartDataPoint[];
  balance_data: BalanceDataPoint[];
}

export interface TransactionTypeOption {
  value: TransactionType;
  label: string;
}

export interface TransactionStatusOption {
  value: TransactionStatus;
  label: string;
}

export interface TransactionTypesResponse {
  transaction_types: TransactionTypeOption[];
  statuses: TransactionStatusOption[];
}

export interface EwalletUser {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  balance: number;
  user_type?: string;
  deleted?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EwalletStatisticsService {
  constructor(private http: HttpClient) {}

  getTransactions(filters: EwalletStatisticsFilters = {}): Observable<TransactionListResponse> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // For arrays, join with comma
          if (value.length > 0) {
            params = params.set(key, value.join(','));
          }
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http.get<TransactionListResponse>('/admin/ewallet_statistics/transactions', { params });
  }

  getChartData(filters: EwalletStatisticsFilters = {}): Observable<ChartDataResponse> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params = params.set(key, value.join(','));
          }
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http.get<ChartDataResponse>('/admin/ewallet_statistics/chart_data', { params });
  }

  searchUsers(query: string): Observable<{ users: EwalletUser[] }> {
    return this.http.get<{ users: EwalletUser[] }>('/admin/ewallet_statistics/search_users', {
      params: { query }
    });
  }

  getTransactionTypes(): Observable<TransactionTypesResponse> {
    return this.http.get<TransactionTypesResponse>('/admin/ewallet_statistics/transaction_types');
  }

  // Helper method to get transaction type display label
  getTransactionTypeLabel(type: TransactionType): string {
    const labels: Record<TransactionType, string> = {
      order_payment: 'Order Payment',
      user_to_user_with_fee: 'User Transfer (with Fee)',
      user_to_user_no_fee: 'User Transfer (no Fee)',
      commission_payment: 'Commission Payment',
      system_commission_payment: 'System Commission',
      account_payment: 'Account Payment',
      payout_payment: 'Payout Payment',
      pool_payout_payment: 'Pool Payout'
    };
    return labels[type] || type;
  }

  // Helper method to get status badge color
  getStatusBadgeColor(status: TransactionStatus): string {
    const colors: Record<TransactionStatus, string> = {
      pending: 'warning',
      completed: 'success',
      failed: 'danger',
      cancelled: 'secondary'
    };
    return colors[status] || 'secondary';
  }
}
